/** "Ask AI" — v1 address-autocomplete.js askAI(): a lightweight assistant that
 * answers a few canned questions straight from the data already in memory (no
 * network round-trip). Same three prompts and the same summarize-from-local-data
 * behavior; wording tuned to the numbers v2 actually has (deals, pipeline,
 * properties, open tasks). */
import { useState } from "react";
import { Icon } from "./icons";

export interface AskAiDeal {
  address: string;
  arv: number;
  purchase: number;
  rehab: number;
}

const money = (n: number): string =>
  n >= 1e6 ? "$" + (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? "$" + Math.round(n / 1e3) + "k" : "$" + Math.round(n);

function highestRoi(deals: AskAiDeal[]): { address: string; roiPct: number; profit: number; d: AskAiDeal } | null {
  let best: { address: string; roiPct: number; profit: number; d: AskAiDeal } | null = null;
  for (const d of deals) {
    const cost = d.purchase + d.rehab;
    if (d.arv <= 0 || cost <= 0) continue;
    const profit = d.arv - cost;
    const roiPct = (profit / cost) * 100;
    if (!best || roiPct > best.roiPct) best = { address: d.address, roiPct, profit, d };
  }
  return best;
}

const QUESTIONS = [
  "Which deals have the highest ROI?",
  "What's in my pipeline right now?",
  "What should I focus on today?",
] as const;

export function AskAiModal({
  deals,
  propertiesCount,
  openTasks,
  onClose,
}: {
  deals: AskAiDeal[];
  propertiesCount: number;
  openTasks: number;
  onClose: () => void;
}) {
  const [answer, setAnswer] = useState<string | null>(null);

  const answerFor = (q: string): string => {
    if (q === QUESTIONS[0]) {
      const top = highestRoi(deals);
      if (!top) return "No deals with both a purchase price and an ARV yet — add those on a deal card and I can rank them by projected ROI.";
      const first = top.address.split(",")[0]?.trim() || top.address;
      return `${first} has the highest projected ROI at ${top.roiPct.toFixed(0)}% — about ${money(top.profit)} of spread (ARV ${money(top.d.arv)}, cost ${money(top.d.purchase + top.d.rehab)}). You have ${deals.length} active deal${deals.length === 1 ? "" : "s"} in the pipeline.`;
    }
    if (q === QUESTIONS[1]) {
      const pipe = deals.reduce((s, d) => s + (d.purchase > 0 ? d.purchase : 0), 0);
      return `${deals.length} active deal${deals.length === 1 ? "" : "s"} totaling ${money(pipe)} in committed purchase price, ${propertiesCount.toLocaleString()} tracked propert${propertiesCount === 1 ? "y" : "ies"}, and ${openTasks} open task${openTasks === 1 ? "" : "s"}.`;
    }
    return openTasks > 0
      ? `You have ${openTasks} open task${openTasks === 1 ? "" : "s"}. Clear those first, then check the Hot list for the highest-motivation leads and any Back-on-market alerts in Notifications.`
      : `No open tasks — nice. Check the Hot list for the highest-motivation leads and scan Notifications for price drops and back-on-market alerts.`;
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <div className="modal askai" onClick={(e) => e.stopPropagation()}>
        <div className="modalhd">
          <div className="askaihd">
            <span className="askaispark"><Icon name="sparkles" size={18} /></span>
            <h2>Ask AI</h2>
          </div>
          <button className="iconbtn" title="Close" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="askaiqs">
          {QUESTIONS.map((q) => (
            <button key={q} className="askaiq" onClick={() => setAnswer(answerFor(q))}>{q}</button>
          ))}
        </div>
        {answer ? <div className="askaiout">{answer}</div> : null}
        <p className="askainote">Answers are computed from your live pipeline in this browser — nothing is sent anywhere.</p>
      </div>
    </div>
  );
}
